import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SOLUTIONS, SERVICES, PROJECTS, MANIFESTO, STATS } from "../data/content";
import { Reveal, SectionTag, Marquee, QuoteCTA } from "../components/Shared";

const HERO_LINES = [
  { text: "WE MAKE", color: "var(--text-1)" },
  { text: "BRANDS", color: "var(--brand-orange)" },
  { text: "IMPOSSIBLE", color: "var(--text-1)" },
  { text: "TO IGNORE.", color: "var(--brand-cyan)" },
];

const HeroShapes = () => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const onMove = (e) => setPos({ x: (e.clientX / window.innerWidth - 0.5) * 2, y: (e.clientY / window.innerHeight - 0.5) * 2 });
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);
  const shapes = [
    { c: "var(--brand-orange)", size: 120, top: "12%", right: "22%", depth: 22, delay: 0 },
    { c: "var(--brand-magenta)", size: 88, top: "48%", right: "8%", depth: 38, delay: 0.15, notch: true },
    { c: "var(--brand-cyan)", size: 150, top: "62%", right: "26%", depth: 14, delay: 0.3 },
    { c: "var(--brand-lime)", size: 56, top: "28%", right: "6%", depth: 50, delay: 0.45 },
  ];
  return (
    <div ref={ref} className="absolute inset-0 pointer-events-none hidden lg:block" aria-hidden>
      {shapes.map((s, i) => (
        <motion.div
          key={i}
          className="absolute"
          initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
          animate={{ opacity: "var(--hero-shape-opacity)", scale: 1, rotate: 0 }}
          transition={{ duration: 1, delay: 0.9 + s.delay, ease: [0.22, 1, 0.36, 1] }}
          style={{
            top: s.top, right: s.right, width: s.size, height: s.size, background: s.c,
            transform: `translate(${pos.x * s.depth}px, ${pos.y * s.depth}px)`,
            transition: "transform 0.6s cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          {s.notch && <span className="corner-notch" />}
        </motion.div>
      ))}
    </div>
  );
};

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / 1600, 1);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);
  return <span ref={ref}>{n}{suffix}</span>;
};

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yBig = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-24 overflow-hidden">
        <HeroShapes />
        <div className="msg-container relative z-10 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="font-mono text-xs tracking-[0.25em] uppercase mb-8"
            style={{ color: "var(--text-3)" }}
            data-testid="hero-kicker"
          >
            Woodbridge, Ontario — Signage & Display Manufacturing
          </motion.p>
          <h1 className="font-display font-black uppercase leading-[0.92] tracking-tight text-4xl sm:text-5xl lg:text-6xl xl:text-[5.5rem]" data-testid="hero-title">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  style={{ color: line.color }}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.7 }}
            className="mt-8 text-base md:text-lg max-w-xl leading-relaxed"
            style={{ color: "var(--text-2)" }}
          >
            MSG — Mark Signs & Graphics is a full-service sign and display manufacturer.
            From POP displays to backlit storefronts, we design, print, fabricate, and install
            everything under one roof.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.35, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/quote" className="btn-brand" data-testid="hero-quote-btn">Request a Quote <ArrowRight size={16} /></Link>
            <Link to="/portfolio" className="btn-ghost" data-testid="hero-work-btn">See Our Work <ArrowUpRight size={16} /></Link>
          </motion.div>
        </div>
        <motion.div style={{ y: yBig }} className="absolute -bottom-6 left-0 right-0 pointer-events-none" aria-hidden>
          <p className="big-outline-text text-[16vw] leading-none whitespace-nowrap opacity-60">MARK SIGNS & GRAPHICS</p>
        </motion.div>
      </section>

      <Marquee />

      {/* SERVICES GRID */}
      <section className="py-28" data-testid="home-services-section">
        <div className="msg-container">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <SectionTag>What We Do</SectionTag>
              <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl mt-5">Design. Print.<br />Fabricate. Install.</h2>
            </div>
            <Link to="/services" className="btn-ghost" data-testid="home-all-services-btn">All Services <ArrowRight size={14} /></Link>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 border-t border-l" style={{ borderColor: "var(--line)" }}>
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.08}>
                <Link
                  to="/services"
                  className="group block p-8 border-r border-b h-full transition-colors hover:bg-[var(--bg-2)]"
                  style={{ borderColor: "var(--line)" }}
                  data-testid={`home-service-card-${s.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="w-12 h-12 flex items-center justify-center border transition-colors group-hover:border-current" style={{ borderColor: "var(--line)", color: s.color }}>
                      <s.icon size={22} />
                    </span>
                    <span className="font-mono text-xs" style={{ color: s.color }}>{s.num}</span>
                  </div>
                  <h3 className="font-display font-bold uppercase text-lg mt-5 mb-3">{s.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--text-2)" }}>{s.desc.slice(0, 90)}…</p>
                  <ArrowUpRight size={18} className="mt-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" style={{ color: s.color }} />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="py-28" style={{ background: "var(--bg-2)" }} data-testid="home-manifesto-section">
        <div className="msg-container">
          <Reveal><SectionTag>Our Manifesto</SectionTag></Reveal>
          <div className="mt-12 space-y-0">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.num} delay={i * 0.05}>
                <div className="grid grid-cols-12 gap-6 py-10 border-t items-start" style={{ borderColor: "var(--line)" }}>
                  <div className="col-span-2 lg:col-span-1 flex flex-col items-start gap-3">
                    <span className="w-11 h-11 flex items-center justify-center" style={{ background: ["var(--brand-orange)", "var(--brand-magenta)", "var(--brand-cyan)", "var(--brand-lime)"][i], color: "#0A0A0C" }}>
                      <m.icon size={20} />
                    </span>
                    <span className="font-mono text-sm" style={{ color: ["var(--brand-orange)", "var(--brand-magenta)", "var(--brand-cyan)", "var(--brand-lime)"][i] }}>{m.num}</span>
                  </div>
                  <h3 className="col-span-10 lg:col-span-4 font-display font-black uppercase tracking-tight text-3xl lg:text-4xl">{m.title}</h3>
                  <p className="col-span-12 lg:col-span-6 lg:col-start-7 text-base md:text-lg leading-relaxed" style={{ color: "var(--text-2)" }}>{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="py-28" data-testid="home-projects-section">
        <div className="msg-container">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <SectionTag>Featured Projects</SectionTag>
              <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl mt-5">Built. Installed.<br />Glowing.</h2>
            </div>
            <Link to="/portfolio" className="btn-ghost" data-testid="home-all-work-btn">Full Portfolio <ArrowRight size={14} /></Link>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PROJECTS.slice(0, 3).map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <Link to="/portfolio" className="msg-card block group" data-testid={`home-project-card-${p.id}`}>
                  <div className="img-frame aspect-[4/3] relative">
                    <img src={p.image} alt={p.title} loading="lazy" />
                    <span className="absolute bottom-4 left-4 z-10 font-mono text-[10px] tracking-widest uppercase px-3 py-1.5" style={{ background: "var(--brand-lime)", color: "#0A0A0C" }}>{p.category}</span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display font-bold uppercase text-lg">{p.title}</h3>
                    <p className="text-sm mt-2" style={{ color: "var(--text-3)" }}>{p.client}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-24 border-t border-b" style={{ borderColor: "var(--line)" }} data-testid="home-stats-section">
        <div className="msg-container grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08}>
              <p className="font-display font-black text-4xl sm:text-5xl lg:text-6xl" style={{ color: ["var(--brand-orange)", "var(--brand-magenta)", "var(--brand-cyan)", "var(--brand-lime)"][i] }}>
                <Counter value={s.value} suffix={s.suffix} />
              </p>
              <p className="font-mono text-xs tracking-[0.15em] uppercase mt-3" style={{ color: "var(--text-2)" }}>{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* KEY STRENGTHS */}
      <section className="py-28" data-testid="home-strengths-section">
        <div className="msg-container grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <SectionTag>Why MSG</SectionTag>
            <h2 className="font-display font-black uppercase tracking-tight text-2xl sm:text-3xl lg:text-4xl mt-5 leading-tight">One Roof.<br />Zero Handoffs.</h2>
            <p className="mt-6 text-base md:text-lg leading-relaxed" style={{ color: "var(--text-2)" }}>
              Most sign shops outsource half the job. We don't. Design, printing, fabrication, and
              installation happen inside our Woodbridge facility — which means tighter timelines,
              consistent quality, and one accountable partner from concept to install.
            </p>
            <ul className="mt-8 space-y-4">
              {["In-house design-to-install workflow", "Grand-format UV & fabric printing", "CNC fabrication & LED integration", "Certified GTA-wide installation crews"].map((t, i) => (
                <li key={t} className="flex items-center gap-4 text-sm font-medium">
                  <span className="w-3 h-3 shrink-0" style={{ background: ["var(--brand-orange)", "var(--brand-magenta)", "var(--brand-cyan)", "var(--brand-lime)"][i] }} />
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="grid grid-cols-2 gap-4">
              {SOLUTIONS.slice(0, 4).map((s, i) => (
                <div key={s.id} className={`img-frame aspect-square ${i % 2 === 1 ? "mt-8" : ""} relative`}>
                  <img src={s.image} alt={s.title} loading="lazy" />
                  <span className="absolute bottom-3 left-3 z-10 font-mono text-[10px] tracking-widest uppercase text-white">{s.title}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <QuoteCTA />
    </div>
  );
}
